import React, { useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Column from "@amzn/meridian/esm/components/column";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Box from "@amzn/meridian/esm/components/box";
import { Divider, Icon, SideMenu, SideMenuLink, SideMenuSpacer, Theme } from "@amzn/meridian";
import calendarTokens from "@amzn/meridian-tokens/base/icon/calendar";
import SidebarInClient from "../common/SidebarInClient";
import SummaryFeedback from "../../common/feedback/SummaryFeedback";
import SummaryListButton from "../../common/summary/SummaryListButton";
import SummaryBodyInDetailPage from "../../common/summary/SummaryBodyInDetailPage";
import SummaryNotApprovedFeedback from "../../common/feedback/SummaryNotApprovedFeedback";
import { SUMMARY_FEEDBACK_STATUS, SUMMARY_TYPES } from "../../common/summary/constants";
import AttendeeListInDetailPage from "../../common/summary/AttendeeListInDetailPage";

const SummaryDetailInClient = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState("summaries");
  const fromPage = location.state?.fromPage || 1;

  const summary = location.state?.summary;
  const summaryFeedbackStatus = SUMMARY_TYPES.APPROVED.includes(summary?.summaryType) 
    ? SUMMARY_FEEDBACK_STATUS.APPROVED
    : SUMMARY_TYPES.NOT_APPROVED.includes(summary?.summaryType)
    ? SUMMARY_FEEDBACK_STATUS.NOT_APPROVED
    : SUMMARY_FEEDBACK_STATUS.OTHER;

  if (!summary || summary.aimsUuid !== id) {
    return (
      <Row spacing="none" spacingInset="none">
        <SideMenu>
          <SideMenuLink selected>Summaries</SideMenuLink>
          <SideMenuLink>Help</SideMenuLink>
          {[...Array(10)].map((_, idx) => (
            <SideMenuSpacer key={idx} />
          ))}
        </SideMenu>
        <Column padding="medium">
          <Text type="b100" color="error">
            Summary not found.
          </Text>
        </Column>
      </Row>
    );
  }

  const onSummaryListInClient = () => {
    console.log("fromPage received in detail:", fromPage);
    console.log("selectedTab is:", selectedTab);
    navigate("/client", {
      state: {
        fromDetail: true,
        returnPage: fromPage,
        selectedTab: selectedTab,
      },
    });
  };

  return (
    <Row spacing="none" spacingInset="none" alignmentVertical="top">
      <Theme tokens={{ boxBorderColorOutline: "#FFFFFF" }}>
        {/*Sidebar box*/}
        <Box type="outline" spacing="none" spacingInset="none" minHeight={557} minWidth={176}>
          <SidebarInClient
            selectedTab={selectedTab}
            setSelectedTab={(tab) => {
              setSelectedTab(tab);
              navigate("/client", {
                state: {
                  fromDetail: true,
                  selectedTab: tab,
                  returnPage: fromPage,
                },
              });
            }}
          />
        </Box>

        <Row width="32px" />

        <Box type="outline" spacing="none" spacingInset="none" minHeight={557} minWidth={358}>
          {/* Summary list button */}
          <Box type="outline" spacing="none" spacingInset="none" minHeight={88} minWidth={358}>
            <Column spacing="none" spacingInset="none">
              <Column height="32px" />
              <SummaryListButton onSummaryListInClient={onSummaryListInClient} />
            </Column>
          </Box>

          {/* Main content row for summary */}
          <Row widths={["32px", "326px", "fill"]} spacing="none" spacingInset="none">
            <Row width="32px" />

            {/*Scroll bar section*/}
            <Box
              width="326px"
              // type="outline"
              height="75vh"
              spacing="none"
              spacingInset="none"
              overflowY="scroll"
            >
              <Column spacing="none" spacingInset="none" width="306px">
                {/* Summary title */}
                <>
                  <style>
                    {`
                      @font-face {
                        font-family: 'Amazon Ember Serif';
                        src: url("https://m.media-amazon.com/images/G/01/kindle/rewards/home/2022_beta/fonts/AmazonEmberSerif_Bd.woff") format("woff"), 
                          url("https://m.media-amazon.com/images/G/01/kindle/rewards/home/2022_beta/fonts/AmazonEmberSerif_Bd.woff") format("woff2");
                        font-weight: 700;
                      }
                    `}
                  </style>
                  <Theme tokens={{ textDefaultFontFamily: ["Amazon Ember Serif"] }}>
                  <Text type="h200">
                    {summary.meetingTitle}
                  </Text>
                  </Theme>
                </>

                <Column height="20px" />

                {/* Summary time with calendar icon */}
                <Row spacing="none" spacingInset="none">
                  <Icon tokens={calendarTokens} color="secondary" />
                  <Row width="4px" />
                  <Text type="b100" color="secondary" fontFamily="amazonEmber" className="summary-time">
                    {summary.meetingDateAndTime}
                  </Text>
                </Row>

                <Column height="8px" />

                {/* Attendee list */}
                <AttendeeListInDetailPage attendeeNameList={summary.attendeeNameList} />

                <Column height="16px" />
                <Divider spacingBefore="0" spacingAfter="0" />
                <Column height="16px" />

                <SummaryBodyInDetailPage summary={summary} isTopicNotApproved={summaryFeedbackStatus === SUMMARY_FEEDBACK_STATUS.NOT_APPROVED}/>
              </Column>
            </Box>
          </Row>

          {/* Feedback */}
          {(!summary.feedback && !summary.feedbackComment) && (
            summaryFeedbackStatus === SUMMARY_FEEDBACK_STATUS.APPROVED ? (
              <SummaryFeedback aimsUuid={id} />
            ) : summaryFeedbackStatus === SUMMARY_FEEDBACK_STATUS.NOT_APPROVED ? (
              <SummaryNotApprovedFeedback aimsUuid={id} />
            ) : null
          )}
        </Box>
      </Theme>
    </Row>
  );
};

export default SummaryDetailInClient;
